import { useViewport } from '@tryhackme/thm-ui-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { useStepper } from 'src/common/hooks/use-stepper-modal';
import { DismissUserDashboardNotificationItems } from 'src/common/interfaces';
import { useTrackEventMutation, useUpdateDashboardNotificationMutation } from 'src/common/slices';
import { useReprofilingState } from '../../hooks/use-reprofiling-state';
import { useGetUserReprofilingRoomRecommendationQuery } from '../../slices/user-reprofiling.slice';
import { ReprofilingFooter } from '../footer';
import { StyledStepperModal } from './reprofiling-modal.styles';
import type { ReprofilingModalProps, StepData } from './reprofiling-modal.types';
import { ReprofilingNextMission, ReprofilingOccupation, ReprofilingStart, ReprofilingTechnicalAbility } from './steps';
export const ReprofilingModal = ({
  open,
  onOpenChange,
  user
}: ReprofilingModalProps) => {
  const navigate = useNavigate();
  const {
    width
  } = useViewport();
  const isMobile = width < BREAKPOINTS.DESKTOP;
  const {
    getStepper
  } = useStepper();
  const {
    suggestion,
    selection,
    onSelectionChange,
    occupationSuggestion,
    occupationSelection,
    onOccupationSelectionChange
  } = useReprofilingState(user?.demographics?.experience, user?.business?.occupation);
  const [trackEvent] = useTrackEventMutation();
  const [dismissNotification] = useUpdateDashboardNotificationMutation();
  const currentPath = useLocation().pathname;
  const {
    data: userReprofilingRoomRecommendation
  } = useGetUserReprofilingRoomRecommendationQuery(undefined, {
    skip: !user
  });
  useEffect(() => {
    if (currentPath !== '/dashboard') {
      onOpenChange(false);
    }
  }, [currentPath, onOpenChange]);
  const suggestedRoom = userReprofilingRoomRecommendation?.data;
  const stepsData: StepData[] = useMemo(() => {
    const baseSteps: StepData[] = [{
      name: 'Name',
      value: user?.username,
      cta: 'Update Profile',
      event: 'update-profile'
    }, {
      name: 'Level',
      cta: "That's me!",
      event: 'thats-me'
    }, {
      name: 'Occupation',
      cta: 'All set!',
      event: 'all-set'
    }];
    if (suggestedRoom) {
      baseSteps.push({
        name: 'next-mission',
        cta: 'Launch!',
        event: 'launch'
      });
    }
    return baseSteps;
  }, [user?.username, suggestedRoom]);
  const amountOfSteps = stepsData.length;
  const {
    goToStep,
    currentStep
  } = getStepper('reprofiling-modal', amountOfSteps, 0);
  const handleBack = useCallback(() => {
    goToStep(currentStep - 1);
  }, [currentStep, goToStep]);
  if (!user) return null;
  const handleShowMore = () => {
    const step = stepsData[currentStep];
    if (!step?.event) return;
    const getTrackingProperties = () => {
      switch (step.event) {
        case 'all-set':
          return {
            properties: {
              old_occupation: occupationSuggestion,
              user_assigned_occupation: occupationSelection
            }
          };
        case 'thats-me':
          return {
            properties: {
              suggested_segment: suggestion,
              user_assigned_segment: selection
            }
          };
        default:
          return {};
      }
    };
    trackEvent({
      event: `reprofile-${step.event}`,
      ...getTrackingProperties()
    });
    const isFinalStep = currentStep === amountOfSteps - 1;
    if (isFinalStep) {
      const searchParams = new URLSearchParams({
        'auto-join': 'true'
      });
      dismissNotification({
        notification: DismissUserDashboardNotificationItems.REPROFILE_NOTIFICATION
      });
      if (suggestedRoom) {
        navigate(`/room/${suggestedRoom?.code}?${searchParams.toString()}`, {
          state: {
            pageReferrer: 'reprofile-modal'
          }
        });
      }
      onOpenChange(false);
    } else goToStep(currentStep + 1);
  };
  const reprofilingFooter = <ReprofilingFooter amountOfSteps={amountOfSteps} currentStep={currentStep} onNext={handleShowMore} onBack={handleBack} stepsData={stepsData} />;
  return <StyledStepperModal open={open} onOpenChange={onOpenChange} id="reprofiling-modal" padding="0" overflowVisible {...isMobile ? {
    maxHeight: 'auto'
  } : {
    minHeight: '63rem'
  }} steps={[{
    id: 'start',
    component: <ReprofilingStart name={user.username} />,
    stepControllers: reprofilingFooter
  }, {
    id: 'level',
    component: <ReprofilingTechnicalAbility ourSuggestion={suggestion} selectedExperience={selection} onSelectedExperienceChange={onSelectionChange} />,
    stepControllers: reprofilingFooter
  }, {
    id: 'occupation',
    component: <ReprofilingOccupation userData={user} ourSuggestion={occupationSuggestion} selectedOccupation={occupationSelection} onSelectedOccupationChange={onOccupationSelectionChange} />,
    stepControllers: reprofilingFooter
  }, ...(suggestedRoom ? [{
    id: 'next-mission',
    component: <ReprofilingNextMission suggestedRoom={suggestedRoom} onLaunch={handleShowMore} />,
    stepControllers: reprofilingFooter
  }] : [])]} data-sentry-element="StyledStepperModal" data-sentry-component="ReprofilingModal" data-sentry-source-file="reprofiling-modal.tsx" />;
};